import { deriveCustomerImpactSummary } from './customerImpact';
import { deriveSiteImpactSummary } from './siteImpact';
import { buildSiteCatalog, resolveCanonicalSiteIdentity } from './siteIdentity';

const toArray = (value) => (Array.isArray(value) ? value : []);

const severityRank = { critical: 3, high: 2, medium: 1, low: 0 };
const statusBuckets = ['critical_now', 'needs_attention', 'active_handling', 'watch'];

const toneBySeverity = (severity) => {
  const token = String(severity || '').toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

const hoursSince = (timestamp) => {
  if (!timestamp) return Number.POSITIVE_INFINITY;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return Number.POSITIVE_INFINITY;
  return Math.max(0, (Date.now() - parsed.getTime()) / 3600000);
};

const latestLogForMatcher = (logs, matcher) => {
  const found = toArray(logs).find((entry) => matcher(entry));
  return found || null;
};

const resolveBucket = ({ severity, lastUpdateAt, impactScore }) => {
  const ageHours = hoursSince(lastUpdateAt);
  if (severity === 'critical' || impactScore >= 20) return 'critical_now';
  if (ageHours > 4) return 'needs_attention';
  if (ageHours <= 1.5) return 'active_handling';
  return 'watch';
};

const normalizeTicketPriority = (priority) => {
  const token = String(priority || '').toLowerCase();
  if (token === 'critical') return 'critical';
  if (token === 'high') return 'high';
  if (token === 'medium') return 'medium';
  return 'low';
};

const siteImpactScore = (site) => (
  (Number(site?.offline_count || 0) * 4)
  + (Number(site?.degraded_count || 0) * 2)
  + (Number(site?.active_incident_count || 0) * 3)
  + (Number(site?.active_ticket_count || 0) * 2)
);

const customerImpactScore = (summary) => (
  (Number(summary?.counts?.offline || 0) * 3)
  + (Number(summary?.counts?.degraded || 0) * 2)
  + Number(summary?.counts?.warning || 0)
);

const buildRankingReasons = ({
  severity,
  sourceType,
  impactScore,
  hasLinkedActiveTicket = false,
  lastUpdate = null,
}) => {
  const reasons = [];
  const severityToken = String(severity || '').toLowerCase();
  if (severityToken === 'critical') reasons.push('Critical incident');
  if (severityToken === 'high' && sourceType === 'ticket') reasons.push('High priority ticket');
  if (impactScore >= 16) reasons.push('High impact');
  if (sourceType === 'customer' && impactScore >= 8) reasons.push('Customer impact high');
  if (sourceType === 'site' && impactScore >= 14) reasons.push('Site impact high');
  if (hasLinkedActiveTicket) reasons.push('Active ticket linked');

  const ageHours = hoursSince(lastUpdate?.at || lastUpdate?.createdAt || lastUpdate?.created_at);
  if (!lastUpdate || ageHours > 6) reasons.push('No recent update');
  if (lastUpdate && String(lastUpdate.label || lastUpdate.actionType || '').toLowerCase() === 'mitigation_completed') {
    reasons.push('Recent mitigation');
  }
  if (lastUpdate && ageHours <= 2 && !reasons.includes('Recent mitigation')) {
    reasons.push('Active handling update');
  }
  return reasons.slice(0, 3);
};

export const triageBucketLabel = (bucket) => {
  if (bucket === 'critical_now') return 'Critical Now';
  if (bucket === 'needs_attention') return 'Needs Attention';
  if (bucket === 'active_handling') return 'Active Handling';
  return 'Watch';
};

export const buildTriageQueue = ({
  incidents = [],
  tickets = [],
  customers = [],
  odps = [],
  olts = [],
  nasRouters = [],
  sites = [],
  executionLogs = [],
}) => {
  const siteCatalog = buildSiteCatalog(sites);
  const toCanonicalSiteId = (source = {}) => String(
    resolveCanonicalSiteIdentity(source, siteCatalog)?.site_id || '',
  ).trim();

  const activeIncidents = toArray(incidents).filter((item) => !['closed', 'mitigated', 'resolved'].includes(String(item?.status || '').toLowerCase()));
  const activeTickets = toArray(tickets).filter((item) => String(item?.status || '').toLowerCase() !== 'closed');

  const globalSiteImpact = deriveSiteImpactSummary({
    context: { type: 'manual', id: 'triage-root' },
    data: { customers, incidents: activeIncidents, tickets: activeTickets, odps, olts, nasRouters, sites },
    executionLogs,
    limit: 6,
  });
  const globalCustomerImpact = deriveCustomerImpactSummary({
    context: { type: 'manual', id: 'triage-root' },
    data: { customers, odps, olts, nasRouters, sites, incidents: activeIncidents },
  });

  const queueItems = [];

  activeIncidents.slice(0, 8).forEach((incident) => {
    const incidentId = String(incident?.id || '').trim();
    if (!incidentId) return;
    const customerImpact = deriveCustomerImpactSummary({
      context: { type: 'incident', id: incidentId, incident },
      data: { customers, odps, olts, nasRouters, sites, incidents: activeIncidents },
    });
    const relatedTickets = activeTickets.filter((ticket) => (
      String(ticket?.incidentId || '').trim() === incidentId
      || (String(ticket?.sourceType || '').toLowerCase() === 'incident' && String(ticket?.sourceId || '').trim() === incidentId)
    ));
    const latestUpdate = latestLogForMatcher(executionLogs, (entry) => String(entry?.incidentId || '').trim() === incidentId);
    const severity = String(incident?.severity || customerImpact?.severity || 'medium').toLowerCase();
    const impactScore = customerImpactScore(customerImpact) + (relatedTickets.length * 2);
    const bucket = resolveBucket({
      severity,
      lastUpdateAt: latestUpdate?.createdAt,
      impactScore,
    });
    queueItems.push({
      id: `incident-${incidentId}`,
      sourceType: 'incident',
      bucket,
      severity,
      tone: toneBySeverity(severity),
      title: `${incidentId} | ${incident?.title || 'Incident aktif'}`,
      subtitle: incident?.summary || incident?.suspectedCause || 'Incident aktif perlu investigasi.',
      impactedSummary: `Customer ${customerImpact?.counts?.total || 0} | Offline ${customerImpact?.counts?.offline || 0} | Ticket ${relatedTickets.length}`,
      contextSummary: (incident?.site_id || incident?.site) ? `Site ${incident.site_id || incident.site}` : 'Context belum lengkap',
      impactScore,
      reasons: buildRankingReasons({
        severity,
        sourceType: 'incident',
        impactScore,
        hasLinkedActiveTicket: relatedTickets.length > 0,
        lastUpdate: latestUpdate,
      }),
      lastUpdate: latestUpdate
        ? {
          label: latestUpdate.actionType || 'manual_note',
          message: latestUpdate.message || '-',
          actor: latestUpdate.actor || '-',
          at: latestUpdate.createdAt || '',
        }
        : null,
      ctas: [
        { label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(incidentId)}` },
        { label: 'Open in Tickets', to: `/tickets?incidentId=${encodeURIComponent(incidentId)}` },
      ],
      shortcuts: [
        { label: 'Start Investigating', to: `/recovery?incidentId=${encodeURIComponent(incidentId)}&triageAction=start_investigating` },
      ],
      references: {
        incidentId,
        ticketId: relatedTickets[0]?.id || '',
        siteId: toCanonicalSiteId({
          site_id: incident?.site_id,
          siteId: incident?.siteId,
          site: incident?.site,
          site_name: incident?.site_name,
          location: incident?.location,
          address: incident?.address,
        }),
      },
    });
  });

  activeTickets.slice(0, 10).forEach((ticket) => {
    const ticketId = String(ticket?.id || '').trim();
    if (!ticketId) return;
    const priority = normalizeTicketPriority(ticket?.priority);
    const customerImpact = deriveCustomerImpactSummary({
      context: {
        type: String(ticket?.sourceType || 'manual').toLowerCase(),
        id: String(ticket?.sourceId || ''),
        incidentId: String(ticket?.incidentId || ''),
        customerId: String(ticket?.customerId || ''),
        routerId: String(ticket?.routerId || ticket?.nodeId || ''),
        siteId: String(ticket?.siteId || ''),
      },
      data: { customers, odps, olts, nasRouters, sites, incidents: activeIncidents },
    });
    const latestUpdate = latestLogForMatcher(executionLogs, (entry) => String(entry?.ticketId || '').trim() === ticketId);
    const impactScore = customerImpactScore(customerImpact) + (priority === 'critical' ? 8 : priority === 'high' ? 5 : 2);
    const bucket = resolveBucket({
      severity: priority,
      lastUpdateAt: latestUpdate?.createdAt || ticket?.updated_at,
      impactScore,
    });
    queueItems.push({
      id: `ticket-${ticketId}`,
      sourceType: 'ticket',
      bucket,
      severity: priority,
      tone: toneBySeverity(priority),
      title: `${ticket?.ticketCode || ticketId} | ${String(ticket?.status || 'open').toLowerCase()}`,
      subtitle: ticket?.title || 'Ticket aktif',
      impactedSummary: `Priority ${priority.toUpperCase()} | PIC ${ticket?.assignedTo || '-'} | Customer ${customerImpact?.counts?.total || 0}`,
      contextSummary: `${String(ticket?.sourceType || 'manual').toLowerCase()}:${String(ticket?.sourceId || '-')}`,
      impactScore,
      reasons: buildRankingReasons({
        severity: priority,
        sourceType: 'ticket',
        impactScore,
        hasLinkedActiveTicket: true,
        lastUpdate: latestUpdate || { at: ticket?.updated_at },
      }),
      lastUpdate: latestUpdate
        ? {
          label: latestUpdate.actionType || 'manual_note',
          message: latestUpdate.message || '-',
          actor: latestUpdate.actor || '-',
          at: latestUpdate.createdAt || '',
        }
        : null,
      ctas: [
        { label: 'Open in Tickets', to: `/tickets?ticketId=${encodeURIComponent(ticketId)}` },
        ticket?.incidentId
          ? { label: 'Open in Recovery', to: `/recovery?incidentId=${encodeURIComponent(String(ticket.incidentId || ''))}` }
          : { label: 'Open in Topology', to: `/topology?mode=live&sourceType=${encodeURIComponent(String(ticket?.sourceType || 'manual').toLowerCase())}&sourceId=${encodeURIComponent(String(ticket?.sourceId || ''))}` },
      ],
      shortcuts: [
        { label: 'Start Investigating', to: `/tickets?ticketId=${encodeURIComponent(ticketId)}&quickAction=investigating` },
      ],
      references: {
        ticketId,
        incidentId: String(ticket?.incidentId || ''),
        customerId: String(ticket?.customerId || ''),
        siteId: toCanonicalSiteId({
          site_id: ticket?.site_id,
          siteId: ticket?.siteId,
          site: ticket?.site,
          site_name: ticket?.site_name,
          location: ticket?.location,
          address: ticket?.address,
        }),
        routerId: String(ticket?.routerId || ticket?.nodeId || ''),
      },
    });
  });

  toArray(globalSiteImpact?.topSites).slice(0, 5).forEach((site) => {
    const siteId = String(site?.site_id || '').trim();
    if (!siteId) return;
    const latestUpdate = site?.last_operational_update || latestLogForMatcher(executionLogs, (entry) => String(entry?.siteId || '').trim() === siteId);
    const severity = String(site?.severity || 'low').toLowerCase();
    const impactScore = siteImpactScore(site);
    const bucket = resolveBucket({
      severity,
      lastUpdateAt: latestUpdate?.createdAt || latestUpdate?.created_at,
      impactScore,
    });
    queueItems.push({
      id: `site-${siteId}`,
      sourceType: 'site',
      bucket,
      severity,
      tone: toneBySeverity(severity),
      title: `${site?.site_name || siteId} | ${severity.toUpperCase()}`,
      subtitle: site?.mitigation_hint || 'Site impact aktif',
      impactedSummary: `Customer ${site?.impacted_customers_total || 0} | Offline ${site?.offline_count || 0} | Ticket ${site?.active_ticket_count || 0}`,
      contextSummary: `Incident ${site?.active_incident_count || 0} | Node ${site?.related_router_count || 0}`,
      impactScore,
      reasons: buildRankingReasons({
        severity,
        sourceType: 'site',
        impactScore,
        hasLinkedActiveTicket: Number(site?.active_ticket_count || 0) > 0,
        lastUpdate: latestUpdate,
      }),
      lastUpdate: latestUpdate
        ? {
          label: latestUpdate.actionType || latestUpdate.action_type || 'manual_note',
          message: latestUpdate.message || '-',
          actor: latestUpdate.actor || '-',
          at: latestUpdate.createdAt || latestUpdate.created_at || '',
        }
        : null,
      ctas: [
        { label: 'Open in Topology', to: `/topology?mode=live&siteId=${encodeURIComponent(siteId)}&openDetail=1` },
        { label: 'Open in Infrastructure', to: `/infrastructure?tab=sites&siteId=${encodeURIComponent(siteId)}` },
      ],
      shortcuts: [
        { label: 'Open relevant ticket', to: `/tickets?siteId=${encodeURIComponent(siteId)}` },
      ],
      references: {
        siteId,
      },
    });
  });

  toArray(globalCustomerImpact?.topCustomers).slice(0, 3).forEach((customer) => {
    const customerId = String(customer?.id || '').trim();
    if (!customerId) return;
    const latestUpdate = latestLogForMatcher(executionLogs, (entry) => String(entry?.customerId || '').trim() === customerId);
    const severity = String(customer?.state === 'offline' ? 'high' : customer?.state === 'degraded' ? 'medium' : 'low').toLowerCase();
    const impactScore = customer?.state === 'offline' ? 8 : customer?.state === 'degraded' ? 5 : 2;
    const bucket = resolveBucket({
      severity,
      lastUpdateAt: latestUpdate?.createdAt,
      impactScore,
    });
    queueItems.push({
      id: `customer-${customerId}`,
      sourceType: 'customer',
      bucket,
      severity,
      tone: toneBySeverity(severity),
      title: `${customer?.name || customer?.code || customerId} | ${String(customer?.state || 'unknown').toUpperCase()}`,
      subtitle: `Customer ${customer?.code || customerId}`,
      impactedSummary: `Site ${customer?.site_name || customer?.site || customer?.site_id || '-'} | Status ${String(customer?.state || 'unknown').toUpperCase()}`,
      contextSummary: 'Customer reverse impact',
      impactScore,
      reasons: buildRankingReasons({
        severity,
        sourceType: 'customer',
        impactScore,
        lastUpdate: latestUpdate,
      }),
      lastUpdate: latestUpdate
        ? {
          label: latestUpdate.actionType || 'manual_note',
          message: latestUpdate.message || '-',
          actor: latestUpdate.actor || '-',
          at: latestUpdate.createdAt || '',
        }
        : null,
      ctas: [
        { label: 'View Customer', to: `/customers?customerId=${encodeURIComponent(customerId)}` },
        { label: 'Open in Recovery', to: `/recovery?customerId=${encodeURIComponent(customerId)}` },
      ],
      shortcuts: [
        { label: 'Open relevant ticket', to: `/tickets?customerId=${encodeURIComponent(customerId)}` },
      ],
      references: {
        customerId,
        siteId: toCanonicalSiteId({
          site_id: customer?.site_id,
          siteId: customer?.siteId,
          site: customer?.site,
          site_name: customer?.site_name,
          location: customer?.location,
          address: customer?.address,
        }),
      },
    });
  });

  const deduped = [];
  const seen = new Set();
  queueItems.forEach((item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    deduped.push(item);
  });

  deduped.sort((a, b) => {
    const bucketRank = { critical_now: 3, needs_attention: 2, active_handling: 1, watch: 0 };
    const diffBucket = (bucketRank[b.bucket] || 0) - (bucketRank[a.bucket] || 0);
    if (diffBucket !== 0) return diffBucket;
    const diffSeverity = (severityRank[b.severity] || 0) - (severityRank[a.severity] || 0);
    if (diffSeverity !== 0) return diffSeverity;
    return Number(b.impactScore || 0) - Number(a.impactScore || 0);
  });

  const summary = {
    total: deduped.length,
    criticalNow: deduped.filter((item) => item.bucket === 'critical_now').length,
    activeHandling: deduped.filter((item) => item.bucket === 'active_handling').length,
    highImpact: deduped.filter((item) => Number(item.impactScore || 0) >= 14).length,
    noRecentUpdate: deduped.filter((item) => !item.lastUpdate || hoursSince(item.lastUpdate.at) > 6).length,
  };

  return {
    summary,
    items: deduped,
    buckets: statusBuckets.reduce((acc, key) => {
      acc[key] = deduped.filter((item) => item.bucket === key);
      return acc;
    }, {}),
    siteImpact: globalSiteImpact,
  };
};
